import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  IconButton,
  Checkbox,
  ListItemText,
  Typography,
  Slide,
  Grid,
} from "@mui/material";

import { grey } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import {
  DataGrid,
  GridColumnMenu,
  GridToolbarQuickFilter,
  gridClasses,
} from "@mui/x-data-grid";
import { setMessageSnackbarStateState } from "../../../../redux/slices/common";
import { setMessage } from "../../../../redux/slices/message";
import UserService from "../../../../services/api/user.service";
import TextField from "@mui/material/TextField";

import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { WarningAmber as WarningIcon } from "@mui/icons-material";
import SettingsIcon from "@mui/icons-material/Settings";

const Transitions = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  loading,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transitions}
      BackdropProps={{
        sx: {
          backdropFilter: "blur(6px)",
          backgroundColor: "rgba(0, 0, 0, 0.4)",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          background: "rgba(255, 255, 255, 0.12)",
          backdropFilter: "blur(20px)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          border: "1px solid rgba(255,255,255,0.2)",
          color: "#fff",
          p: 2,
          minWidth: "400px",
        },
      }}>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontWeight: "bold",
          fontSize: "1.3rem",
        }}>
        <WarningIcon sx={{ color: "#FFD700", fontSize: 30 }} />
        {title}
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ fontSize: "1rem", opacity: 0.9, mt: 1 }}>
          {message}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", mt: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            color: grey[700],
            fontWeight: 500,
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.05)",
            },
          }}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading}
          sx={{
            background: "linear-gradient(135deg, #FF4B2B, #FF416C)",
            borderRadius: "8px",
            px: 3,
            fontWeight: "bold",
            "&:hover": {
              background: "linear-gradient(135deg, #E63E24, #E23B60)",
            },
          }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MAX_TOPUP_LIMIT = 200000;

function SettingModal({ open, onClose, userData, onRefresh }) {
  const [amount, setAmount] = useState("");
  // const [audienceCost, setAudienceCost] = useState("");
  const [platformCost, setPlatformCost] = useState("");
  const [cardCost, setCardCost] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const dispatch = useDispatch();
  const { currentTheme } = useSelector((state) => state.theme);

  const formatDateForInput = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return "";
    const [day, month, year] = parts;
    return `${year}-${month}-${day}`;
  };

  const formatDateForApi = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return "";
    const [year, month, day] = parts;
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    if (open && userData) {
      setAmount("");
      // setAudienceCost(userData.audienceCost?.toString() || "");
      setPlatformCost(userData.platformCost?.toString() || "");
      setCardCost(userData.cardCost?.toString() || "");
      setExpiryDate(formatDateForInput(userData.expiryDate));
      setErrors({});
    }
  }, [open, userData]);

  const handleInputChange = (setter, fieldName, value) => {
    setter(value);
    if (errors[fieldName]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const validateInputs = () => {
    const newErrors = {};
    const topUpAmount = Number(amount);
    // const newAudienceCost = Number(audienceCost);
    const newPlatformCost = Number(platformCost);
    const newCardCost = Number(cardCost);

    if (amount && (isNaN(topUpAmount) || topUpAmount < 0)) {
      newErrors.amount = "If entered, must be a valid non-negative amount.";
    } else if (topUpAmount > MAX_TOPUP_LIMIT) {
      newErrors.amount = `Amount cannot exceed $${MAX_TOPUP_LIMIT.toLocaleString()}`;
    }

    // if (isNaN(newAudienceCost) || newAudienceCost < 0.1) {
    //   newErrors.audienceCost = "Audience cost must be at least $0.1";
    // }

    if (isNaN(newPlatformCost) || newPlatformCost < 0.1) {
      newErrors.platformCost = "Platform cost must be at least $0.1";
    }

    if (isNaN(newCardCost) || newCardCost < 1) {
      newErrors.cardCost = "Card cost must be at least $1.";
    }

    if (!expiryDate) {
      newErrors.expiryDate = "Expiry date is required.";
    } else if (
      new Date(expiryDate) < new Date(new Date().setHours(0, 0, 0, 0))
    ) {
      newErrors.expiryDate = "Expiry date cannot be in the past.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async () => {
    if (!validateInputs()) {
      return;
    }

    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        userId: userData.id,
        creditAmount: amount || "0",
        // audienceCost: audienceCost,
        platformCost: platformCost,
        cardCost: cardCost,
        expireDate: formatDateForApi(expiryDate),
      }).toString();

      const data = await UserService.topUpCreditAndCosts(queryParams);
      dispatch(
        setMessage({
          type: "success",
          title: data.message || "Settings updated successfully!",
        })
      );
      dispatch(setMessageSnackbarStateState(true));
      onRefresh();
      onClose();
    } catch (error) {
      dispatch(
        setMessage({
          type: "error",
          title: error.response?.data?.message || "Failed to update settings.",
        })
      );
      dispatch(setMessageSnackbarStateState(true));
    } finally {
      setLoading(false);
    }
  };

  if (!userData) return null;

  const isDarkMode = currentTheme === "dark";
  const currentBalance = Number(userData?.existingLimit || 0);

  const isChanged =
    // Number(audienceCost) !== Number(userData.audienceCost) ||
    Number(platformCost) !== Number(userData.platformCost) ||
    Number(cardCost) !== Number(userData.cardCost) ||
    expiryDate !== formatDateForInput(userData.expiryDate) ||
    (amount !== "" && Number(amount) > 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      BackdropProps={{
        sx: {
          backdropFilter: "blur(6px)",
          backgroundColor: "rgba(0,0,0,0.4)",
        },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "500px",
          borderRadius: "14px",
          backgroundColor: isDarkMode ? "#1E1E2F" : "#FFFFFF",
          boxShadow: "0 10px 35px rgba(0,0,0,0.2)",
        },
      }}>
      <DialogTitle
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          fontSize: "1.5rem",
          background: "linear-gradient(135deg, #3C4DE7, #6574FF)",
          color: "#FFF",
          p: 2,
        }}>
        Pricing
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 2 }}>
        <Box
          sx={{
            p: 2.5,
            my: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : "#F3F6FF",
            borderRadius: "10px",
            boxShadow: "inset 0 0 8px rgba(71, 69, 69, 0.05)",
          }}>
          <AccountBalanceWalletIcon
            sx={{ fontSize: 42, color: isDarkMode ? "#80AFFF" : "#3C4DE7" }}
          />
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "600", color: isDarkMode ? "#FFF" : "#333" }}>
              {userData?.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: isDarkMode ? grey[400] : grey[600] }}>
              Current Balance:{" "}
              <strong style={{ color: isDarkMode ? "#80AFFF" : "#2C4DE7" }}>
                $ {currentBalance.toLocaleString()}
              </strong>
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            {/* <TextField
              fullWidth
              type="number"
              label="Audience Cost ($)"
              value={audienceCost}
              onChange={(e) =>
                handleInputChange(
                  setAudienceCost,
                  "audienceCost",
                  e.target.value
                )
              }
              error={!!errors.audienceCost}
              helperText={errors.audienceCost || ""}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: isDarkMode ? "#2C2C4A" : "#FFF",
                },
                input: { color: isDarkMode ? "#FFF" : "#000" },
              }}
              InputLabelProps={{
                style: { color: isDarkMode ? grey[400] : grey[600] },
              }}
            /> */}
            <TextField
              fullWidth
              type="number"
              label="Platform Cost ($)"
              value={platformCost}
              onChange={(e) =>
                handleInputChange(
                  setPlatformCost,
                  "platformCost",
                  e.target.value
                )
              }
              error={!!errors.platformCost}
              helperText={errors.platformCost || ""}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: isDarkMode ? "#2C2C4A" : "#FFF",
                },
                input: { color: isDarkMode ? "#FFF" : "#000" },
              }}
              InputLabelProps={{
                style: { color: isDarkMode ? grey[400] : grey[600] },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Card Cost ($)"
              value={cardCost}
              onChange={(e) =>
                handleInputChange(setCardCost, "cardCost", e.target.value)
              }
              error={!!errors.cardCost}
              helperText={errors.cardCost || ""}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: isDarkMode ? "#2C2C4A" : "#FFF",
                },
                input: { color: isDarkMode ? "#FFF" : "#000" },
              }}
              InputLabelProps={{
                style: { color: isDarkMode ? grey[400] : grey[600] },
              }}
            />
          </Grid>
        </Grid>

        <TextField
          fullWidth
          type="date"
          label="Date of Expiry (Optional)"
          value={expiryDate}
          onChange={(e) =>
            handleInputChange(setExpiryDate, "expiryDate", e.target.value)
          }
          error={!!errors.expiryDate}
          helperText={errors.expiryDate || ""}
          variant="outlined"
          sx={{
            mt: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: isDarkMode ? "#2C2C4A" : "#FFF",
            },
            input: { color: isDarkMode ? "#FFF" : "#000" },
          }}
          InputLabelProps={{
            shrink: true,
            style: { color: isDarkMode ? grey[400] : grey[600] },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: "space-between" }}>
        <Button
          onClick={onClose}
          sx={{
            color: isDarkMode ? grey[300] : grey[700],
            fontWeight: 500,
            "&:hover": {
              backgroundColor: isDarkMode
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.05)",
            },
          }}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || !!Object.keys(errors).length || !isChanged}
          sx={{
            background: "linear-gradient(135deg, #3C4DE7, #6574FF)",
            borderRadius: "8px",
            px: 3,
            fontWeight: "600",
            "&:hover": {
              background: "linear-gradient(135deg, #303DBB, #505EE5)",
            },
          }}>
          {loading ? <CircularProgress size={22} color="inherit" /> : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TopUpModal({ open, onClose, userData, onRefresh }) {
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const dispatch = useDispatch();
  const { currentTheme } = useSelector((state) => state.theme);

  useEffect(() => {
    if (open && userData) {
      setAmount("");
      setErrors({});
    }
  }, [open, userData]);

  const handleAmountChange = (e) => {
    let value = e.target.value;
    const numericValue = Number(value);

    if (numericValue > MAX_TOPUP_LIMIT) {
      value = String(MAX_TOPUP_LIMIT);
      setErrors((prev) => ({
        ...prev,
        amount: `Max limit is $${MAX_TOPUP_LIMIT.toLocaleString()}`,
      }));
    } else if (numericValue < 1) {
      setErrors((prev) => ({
        ...prev,
        amount: "Minimum top-up amount is $1.",
      }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.amount;
        return newErrors;
      });
    }

    setAmount(value);
  };

  const validateInputs = () => {
    const newErrors = {};
    const topUpAmount = Number(amount);

    if (isNaN(topUpAmount) || topUpAmount < 1) {
      newErrors.amount = "Minimum top-up amount is $1.";
    } else if (topUpAmount > MAX_TOPUP_LIMIT) {
      newErrors.amount = `Amount cannot exceed $ ${MAX_TOPUP_LIMIT.toLocaleString()}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async () => {
    if (!validateInputs()) return;

    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        userId: userData.id,
        creditAmount: amount || "0",
        // audienceCost: userData.audienceCost,
        platformCost: userData.platformCost,
        cardCost: userData.cardCost
      }).toString();


      const data = await UserService.topUpCreditAndCosts(queryParams);
      dispatch(
        setMessage({
          type: "success",
          title: data.message || "Update successful!",
        })
      );
      dispatch(setMessageSnackbarStateState(true));
      onRefresh();
      onClose();
    } catch (error) {
      dispatch(
        setMessage({
          type: "error",
          title:
            error.response?.data?.message || "Failed to process the update.",
        })
      );
      dispatch(setMessageSnackbarStateState(true));
    } finally {
      setLoading(false);
    }
  };

  if (!userData) return null;

  const isDarkMode = currentTheme === "dark";
  const currentBalance = Number(userData?.existingLimit || 0);
  const enteredAmount = Number(amount) || 0;
  const newBalance =
    enteredAmount >= 1 && enteredAmount <= MAX_TOPUP_LIMIT
      ? currentBalance + enteredAmount
      : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      BackdropProps={{
        sx: {
          backdropFilter: "blur(6px)",
          backgroundColor: "rgba(0,0,0,0.4)",
        },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "500px",
          borderRadius: "14px",
          backgroundColor: isDarkMode ? "#1E1E2F" : "#FFFFFF",
          boxShadow: "0 10px 35px rgba(0,0,0,0.2)",
        },
      }}>
      <DialogTitle
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          fontSize: "1.5rem",
          background: "linear-gradient(135deg, #3C4DE7, #6574FF)",
          color: "#FFF",
          p: 2,
        }}>
        💳 Top-up Credit
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box
          sx={{
            p: 2.5,
            mb: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
            backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : "#F3F6FF",
            borderRadius: "10px",
            boxShadow: "inset 0 0 8px rgba(0,0,0,0.05)",
          }}>
          <AccountBalanceWalletIcon
            sx={{ fontSize: 42, color: isDarkMode ? "#80AFFF" : "#3C4DE7" }}
          />
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "600", color: isDarkMode ? "#FFF" : "#333" }}>
              {userData?.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: isDarkMode ? grey[400] : grey[600] }}>
              Current Balance:{" "}
              <strong style={{ color: isDarkMode ? "#80AFFF" : "#2C4DE7" }}>
                $ {currentBalance.toLocaleString()}
              </strong>
            </Typography>
          </Box>
        </Box>

        <TextField
          autoFocus
          fullWidth
          type="number"
          label="Enter Top-up Amount ($)"
          value={amount}
          onChange={handleAmountChange}
          error={!!errors.amount}
          helperText={errors.amount || ""}
          variant="outlined"
          sx={{
            mt: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: isDarkMode ? "#2C2C4A" : "#FFF",
            },
            input: { color: isDarkMode ? "#FFF" : "#000" },
          }}
          InputLabelProps={{
            style: { color: isDarkMode ? grey[400] : grey[600] },
          }}
        />

        {newBalance !== null && !errors.amount && (
          <Typography
            variant="subtitle2"
            sx={{
              mt: 2,
              textAlign: "right",
              color: isDarkMode ? "#80FFB2" : "#1B873F",
              fontWeight: 600,
            }}>
            Updated Balance: $ {newBalance.toLocaleString()}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: "space-between" }}>
        <Button
          onClick={onClose}
          sx={{
            color: isDarkMode ? grey[300] : grey[700],
            fontWeight: 500,
            "&:hover": {
              backgroundColor: isDarkMode
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.05)",
            },
          }}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || Object.keys(errors).length > 0 || !amount}
          sx={{
            background: "linear-gradient(135deg, #3C4DE7, #6574FF)",
            borderRadius: "8px",
            px: 3,
            fontWeight: "600",
            "&:hover": {
              background: "linear-gradient(135deg, #303DBB, #505EE5)",
            },
          }}>
          {loading ? <CircularProgress size={22} color="inherit" /> : " Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function QuickSearchToolbar() {
  const { currentTheme } = useSelector((state) => state.theme);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        p: 0.5,
        pb: 0,
        "& .MuiInputBase-input": {
          color: currentTheme === "dark" ? "#ffffff" : undefined,
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: "none",
        },
      }}>

      <GridToolbarQuickFilter
        sx={{ border: "none", borderBottom: 0, padding: 0 }}
        autoComplete="off"
      />

    </Box>
  );
}

function CustomColumnMenu(props) {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuFilterItem: null,
      }}
    />
  );
}

function ExternalUserManagementModal() {
  const { user } = useSelector((state) => state.auth.auth);
  const { currentTheme } = useSelector((state) => state.theme);
  const currentThemeColor = currentTheme === "light" ? "#010412" : "#FFF";
  const dispatch = useDispatch();
  const [loadingState, setLoadingState] = useState({});

  const [tableData, setTableData] = useState([]);

  const [topUpModalOpen, setTopUpModalOpen] = useState(false);
  const [settingModalOpen, setSettingModalOpen] = useState(false);

  const [selectedUserForTopUp, setSelectedUserForTopUp] = useState(null);

  const externalUsersCall = async () => {
    try {
      if (user) {
        const data = await UserService.externalUserCall();

        if (data?.results?.length) {
          setTableData(data.results);
        } else {
          setTableData([]);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const countries = [
    { id: 2, name: "UAE" },
    { id: 3, name: "QA" },
    { id: 4, name: "KSA" },
    { id: 8, name: "LB" },
    { id: 9, name: "DE" },
    { id: 10, name: "ES" },
    { id: 5, name: "KW" },
    { id: 11, name: "PT"},
    { id: 6, name: "OM"},
    { id: 7, name: "BH"}
  ];

  useEffect(() => {
    externalUsersCall();
  }, []);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleConfirmToggle = async () => {
    if (!selectedRow) return;
    const { id, isActivePlan } = selectedRow;

    setConfirmLoading(true);
    setLoadingState((prev) => ({ ...prev, [id]: true }));

    try {
      const queryParams = `userId=${id}&isActivate=${!isActivePlan}`;
      await UserService.updateUserStatus(queryParams);

      dispatch(setMessage({ type: "success", title: "User status updated!" }));
      dispatch(setMessageSnackbarStateState(true));
      externalUsersCall();
    } catch (error) {
      dispatch(
        setMessage({
          type: "error",
          title: error.response?.data?.message || "Failed to update status.",
        })
      );
      dispatch(setMessageSnackbarStateState(true));
    } finally {
      setConfirmLoading(false);
      setLoadingState((prev) => ({ ...prev, [id]: false }));
      setConfirmOpen(false);
    }
  };

  const processRowUpdate = (newRow) => {
    const updatedRows = tableData.map((row) =>
      row.id === newRow.id ? newRow : row
    );
    setTableData(updatedRows);
    return newRow;
  };

  const handleProcessRowUpdateError = (error) => {
    console.error(error);
  };

  const genericPreProcessEditCellProps = (params, options) => {
    const value = params.props.value;
    let hasError = false;
    let errorMessage = "";

    if (isNaN(value)) {
      hasError = true;
      errorMessage = "Input is not a number";
    } else if (value === "") {
      hasError = true;
      errorMessage = "Input cannot be empty";
    } else if (Number(value) < 0) {
      hasError = true;
      errorMessage = "Negative values are not allowed";
    } else if (options?.max !== undefined && Number(value) >= options.max) {
      hasError = true;
      errorMessage = `Value must be less than ${options.max}`;
    }

    if (hasError) {
      dispatch(
        setMessage({
          type: "error",
          title: errorMessage,
        })
      );
      dispatch(setMessageSnackbarStateState(true));
    } else if (options?.incrementBy) {
      params.props.value = Number(value) + options.incrementBy;
    }

    return { ...params.props, error: hasError };
  };

  const genericFloatPreProcessEditCellProps = (params) => {
    const { props } = params;
    let value = props.value;
    let hasError = false;
    let errorMessage = "";

    if (isNaN(value)) {
      hasError = true;
      errorMessage = "Input is not a number";
    } else if (value === "") {
      hasError = true;
      errorMessage = "Input cannot be empty";
    } else if (value < 0) {
      hasError = true;
      errorMessage = "Input cannot be negative";
    }

    if (hasError) {
      dispatch(
        setMessage({
          type: "error",
          title: errorMessage,
        })
      );
      dispatch(setMessageSnackbarStateState(true));
    }

    return { ...props, value, hasError };
  };

  const formatDecimal = (value) => {
    if (value !== null && value !== undefined) {
      return value.toFixed(4);
    }
    return "";
  };

  const handleCountryChange = (userId, newCountryIds) => {
    sendSelectedCountriesToAPI(userId, newCountryIds);
  };

  const sendSelectedCountriesToAPI = async (userId, countryIds) => {
    const queryParams = new URLSearchParams({
      userId: userId,
      countryIds: countryIds.join(","),
    });

    try {
      const data = await UserService.updateCountries(queryParams.toString());
      dispatch(
        setMessage({
          type: "success",
          title: data.message,
        })
      );
      dispatch(setMessageSnackbarStateState(true));
      externalUsersCall();
    } catch (error) {
      dispatch(
        setMessage({
          type: "error",
          title: error.response?.data?.message || "An error occurred",
        })
      );
      dispatch(setMessageSnackbarStateState(true));
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const data = await UserService.updateUser(`userId=${id}&status=${newStatus}`);
      dispatch(
        setMessage({
          type: "success",
          title: data.message,
        })
      );
      dispatch(setMessageSnackbarStateState(true));
      setTableData((prevData) => {
        if (newStatus === "DELETED") {
          return prevData.filter((user) => user.id !== id);
        } else {
          return prevData.map((user) =>
            user.id === id ? { ...user, status: newStatus } : user
          );
        }
      });

    } catch (error) {
      console.error("Error updating status:", error);
      dispatch(
        setMessage({
          type: "error",
          title: error.message,
        })
      );
      dispatch(setMessageSnackbarStateState(true));
    }
  };

  const handleOpenTopUpModal = (user) => {
    setSelectedUserForTopUp(user);
    setTopUpModalOpen(true);
  };

  const handleCloseTopUpModal = () => {
    setTopUpModalOpen(false);
    setSelectedUserForTopUp(null);
  };
  const handleOpenSettingModal = (user) => {
    setSelectedUserForTopUp(user);
    setSettingModalOpen(true);
  };

  const handleCloseSettingModal = () => {
    setSettingModalOpen(false);
    setSelectedUserForTopUp(null);
  };
  const columns = [
    {
      field: "name",
      headerName: "Name",
      width: 250,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "companyName",
      headerName: "Company Name",
      width: 200,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "email",
      headerName: "Email",
      width: 250,
      align: "center",
      headerAlign: "center",
      type: "mail",
    },

    // {
    //   field: "audienceCost",
    //   headerName: "Audience Cost",
    //   editable: false,
    //   minWidth: 100,
    //   type: "number",
    //   renderCell: (params) => formatDecimal(params.value),
    //   preProcessEditCellProps: (params) =>
    //     genericFloatPreProcessEditCellProps(params),
    // },
    {
      field: "platformCost",
      headerName: "Platform Cost",
      editable: false,
      minWidth: 100,
      type: "number",
      renderCell: (params) => params.value,
      preProcessEditCellProps: (params) =>
        genericFloatPreProcessEditCellProps(params),
    },
    {
      field: "cardCost",
      headerName: "Card Cost",
      editable: false,
      minWidth: 100,
      type: "number",
      preProcessEditCellProps: (params) =>
        genericPreProcessEditCellProps(params, { max: 1000 }),
    },

    {
      field: "existingLimit",
      headerName: "Balance",
      editable: false,
      minWidth: 100,
      type: "number",
    },
    {
      field: "countryIds",
      headerName: "Market",
      width: 200,
      renderCell: (params) => {
        const { id, countryIds } = params.row;
        const themeColors = {
          light: {
            background: "#e4e4e4",
            text: "#212121",
            border: "#BDBDBD",
            hover: "#E0E0E0",
            checked: "#1976D2",
            checkedText: "#39BF8F",
            uncheckedText: "#000",
          },
          dark: {
            background: "#1E1E2F",
            text: "#c7c7c7",
            border: "#5C5C5C",
            hover: "#33334A",
            checked: "#64B5F6",
            checkedText: "#101110",
            uncheckedText: "#FFF",
          },
        };
        const currentColors =
          currentTheme === "dark" ? themeColors.dark : themeColors.light;

        return (
          <Select
            multiple
            value={countryIds || []}
            onChange={(e) => handleCountryChange(id, e.target.value)}
            renderValue={(selected) =>
              countries
                .filter((country) => selected.includes(country.id))
                .map((country) => country.name)
                .join(", ")
            }
            fullWidth
            sx={{
              backgroundColor: currentColors.background,
              color: currentColors.text,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: currentColors.border,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: currentColors.checked,
              },
            }}>

            {countries.map((country) => {
              const isChecked = (countryIds || []).includes(country.id);
              return (
                <MenuItem
                  key={country.id}
                  value={country.id}
                  sx={{
                    backgroundColor: currentColors.background,
                    color: currentColors.text,
                    "&:hover": { backgroundColor: currentColors.hover },
                  }}>

                  <Checkbox
                    checked={isChecked}
                    sx={{
                      color: currentColors.border,
                      "&.Mui-checked": { color: currentColors.checked },
                    }}
                  />

                  <ListItemText
                    primary={country.name}
                    sx={{
                      color: isChecked
                        ? currentColors.checkedText
                        : currentColors.uncheckedText,
                    }}
                  />

                </MenuItem>
              );
            })}

          </Select>
        );
      },
    },
    {
      field: "topUp",
      headerName: "Top-up Credit",
      width: 80,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => handleOpenTopUpModal(params.row)}
          disabled={params.row.status !== "ACTIVE"}>
          Top-up
        </Button>
      ),
    },
    {
      field: "Setting",
      headerName: "Settings",
      width: 70,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => (
        <IconButton
          onClick={() => handleOpenSettingModal(params.row)}
          disabled={params.row.status !== "ACTIVE"}
          color="primary"
          aria-label="Open settings">
          <SettingsIcon />
        </IconButton>
      ),
    },
    {
      field: "expiryDate",
      headerName: "Date of expiry",
      width: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Select
          value={params.row.status}
          onChange={(e) => handleStatusChange(params.row.id, e.target.value)}
          size="small"
          fullWidth
          sx={{ color: currentThemeColor }}
        >
          {["ACTIVE", "INACTIVE", "ARCHIVE", "PENDING_VERIFICATION", "DELETED"].map(
            (status) => (
              <MenuItem
                key={status}
                value={status}
                disabled={status === params.row.status || status === "PENDING_VERIFICATION"}
              >
                {status.charAt(0) + status.slice(1).toLowerCase().replace("_", " ")}
              </MenuItem>
            )
          )}
        </Select>
      ),
    },
    {
      field: "createdCards",
      headerName: "Cards",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
  ];

  const getRowSpacing = React.useCallback((params) => {
    return {
      top: params.isFirstVisible ? 0 : 5,
      bottom: params.isLastVisible ? 0 : 5,
    };
  }, []);

  const StyledGridOverlay = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    "& .ant-empty-img-1": {
      fill: theme.palette.mode === "light" ? "#aeb8c2" : "#262626",
    },
    "& .ant-empty-img-2": {
      fill: theme.palette.mode === "light" ? "#f5f5f7" : "#595959",
    },
    "& .ant-empty-img-3": {
      fill: theme.palette.mode === "light" ? "#dce0e6" : "#434343",
    },
    "& .ant-empty-img-4": {
      fill: theme.palette.mode === "light" ? "#fff" : "#1c1c1c",
    },
    "& .ant-empty-img-5": {
      fillOpacity: theme.palette.mode === "light" ? "0.8" : "0.08",
      fill: theme.palette.mode === "light" ? "#f5f5f5" : "#fff",
    },
  }));

  function CustomNoRowsOverlay() {
    return (
      <StyledGridOverlay>
        <svg
          style={{ flexShrink: 0 }}
          width="240"
          height="200"
          viewBox="0 0 184 152"
          aria-hidden
          focusable="false">
          <g fill="none" fillRule="evenodd">
            <g transform="translate(24 31.67)">
              <ellipse
                className="ant-empty-img-5"
                cx="67.797"
                cy="106.89"
                rx="67.797"
                ry="12.668"
              />
              <path
                className="ant-empty-img-1"
                d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
              />
              <path
                className="ant-empty-img-2"
                d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
              />
              <path
                className="ant-empty-img-3"
                d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
              />
            </g>
            <path
              className="ant-empty-img-3"
              d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
            />

            <g className="ant-empty-img-4" transform="translate(149.65 15.383)">
              <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
              <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />

            </g>
          </g>
        </svg>
        <Box sx={{ mt: 1, color: currentTheme === "light" ? "#000" : "#FFF" }}>
          No Rows
        </Box>
      </StyledGridOverlay>
    );
  }

  const initialState = {
    pagination: { paginationModel: { pageSize: 10 } },
    columns: {
      columnVisibilityModel: {
        phoneNumber: false,
        createdCards: false,
        cardLimit: false,
      },
    },
  };

  return (
    <>
      <DataGrid
        sx={{
          [`& .${gridClasses.row}`]: {
            borderLeft: "2.5px solid #3C4DE7",
            borderRadius: "0 10px 10px 0",
            bgcolor: currentTheme === "light" ? grey[200] : grey[900],
            color: currentTheme === "light" ? grey[900] : grey[200],
          },
          "& .MuiDataGrid-columnHeaders": {
            border: "none",
            color: grey[600],
            "&:hover": { color: "#3C4DE7" },
          },
          "& .MuiDataGrid-iconSeparator": { display: "none" },
          "& .MuiDataGrid-cell": { border: "none" },
          "& .MuiDataGrid-footerContainer ": { border: "none" },
          "& .MuiTablePagination-displayedRows": { color: currentThemeColor },
          "& .MuiButtonBase-root ": {
            color: currentTheme === "light" ? grey[900] : grey[200],
          },
          "& .MuiDataGrid-selectedRowCount": { color: currentThemeColor },
          "& .MuiDataGrid-virtualScroller": {
            color: currentTheme === "light" ? grey[900] : grey[200],
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            width: "0.4em",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
            background:
              currentTheme === "light"
                ? "rgba(0,0,0,0.1)"
                : "rgba(255,255,255,0.1)",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
            borderRadius: "10px",
            boxShadow: "inset 0 0 6px rgba(0,0,0,.3)",
            backgroundColor: currentTheme === "light" ? "#f5f5f5" : "#494848",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
            background: currentTheme === "light" ? "#494848" : "#f5f5f5",
          },
          border: "none",
          "& .MuiDataGrid-cell.MuiDataGrid-cell--editing": {
            backgroundColor: currentTheme === "light" ? "#f4f1f1" : "#121331",
          },
        }}
        initialState={initialState}
        getRowSpacing={getRowSpacing}
        pageSizeOptions={[10]}
        disableSelectionOnClick
        rows={tableData}
        columns={columns}
        editMode="cell"
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        slots={{
          toolbar: QuickSearchToolbar,
          noRowsOverlay: CustomNoRowsOverlay,
          columnMenu: CustomColumnMenu,
        }}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmToggle}
        title="Confirm Action"
        message={
          selectedRow?.isActivePlan
            ? "Are you sure you want to deactivate this plan?"
            : "Are you sure you want to activate this plan?"
        }
        loading={confirmLoading}
      />
      <TopUpModal
        open={topUpModalOpen}
        onClose={handleCloseTopUpModal}
        userData={selectedUserForTopUp}
        onRefresh={externalUsersCall}
      />
      <SettingModal
        open={settingModalOpen}
        onClose={handleCloseSettingModal}
        userData={selectedUserForTopUp}
        onRefresh={externalUsersCall}
      />
    </>
  );
}

export default ExternalUserManagementModal;
